// ═══════════════════════════════════════════════════════════════════════════
// logger — Logger centralizado del frontend (V3.7.1)
// ═══════════════════════════════════════════════════════════════════════════
//
// API simple con 4 niveles + envío opcional a backend para errors en prod.
//
// Reemplaza el patrón disperso de `console.log/warn/error` que creció a 363+
// callsites. El plan original (Bloque 7.2) es ir migrando esos callsites a
// este logger en una pasada masiva de find/replace.
//
// USO
// ───
//   import { log } from '@core/lib/logger'
//
//   log.debug('mensaje', { ctx })   // dev only
//   log.info('mensaje', { ctx })    // dev: console; prod: silent
//   log.warn('mensaje', { ctx })    // dev + prod: console
//   log.error('mensaje', err, { ctx })  // dev + prod: console + Firestore
//
//   // Logger con scope (prefix automático):
//   const log = createLogger('OpinionesDashboard')
//   log.info('cargado', { count })  // → '[OpinionesDashboard] cargado { count: 42 }'
//
// COMPORTAMIENTO POR NIVEL
// ────────────────────────
//   debug → console.debug solo si LOGGER_DEBUG=true en localStorage o dev mode
//   info  → console.info en dev; noop en prod
//   warn  → console.warn siempre
//   error → console.error siempre + persiste a `clientLogs` Firestore con TTL 30d
//
// PRIVACIDAD
// ──────────
// `log.error()` mete el error en Firestore. NUNCA pasar tokens, passwords,
// session cookies, ni datos PII completos en el `ctx`. La utility `sanitize()`
// ofusca strings que parezcan tokens (heurística básica).
//
// ═══════════════════════════════════════════════════════════════════════════

import {
  collection,
  addDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore'
import { db, auth } from './firebase'
import { urlSegura } from './urlSegura'

const isDev = import.meta.env.DEV
const isProd = import.meta.env.PROD

// Habilitar debug logs en producción setteando localStorage.LOGGER_DEBUG=true.
// Útil para troubleshooting puntual sin redeploy.
function debugEnabled() {
  if (isDev) return true
  try {
    return window.localStorage?.getItem('LOGGER_DEBUG') === 'true'
  } catch {
    return false
  }
}

// Patrón heurístico para enmascarar valores que parecen tokens/secretos.
// No es una solución completa — el caller es responsable de no pasar PII.
const TOKEN_PATTERN = /\b([A-Za-z0-9_-]{32,})\b/g

function sanitizeString(s) {
  if (typeof s !== 'string') return s
  return s.replace(TOKEN_PATTERN, (match) => {
    if (match.length < 32) return match
    return `${match.slice(0, 4)}…${match.slice(-4)}`
  })
}

// `urlSegura` y `RUTAS_CON_CREDENCIAL` viven en `./urlSegura` (módulo puro, con
// espejo en el backend); se re-exportan acá para no mover a sus importadores.
export { urlSegura, RUTAS_CON_CREDENCIAL } from './urlSegura'

function sanitizeCtx(ctx) {
  if (!ctx || typeof ctx !== 'object') return ctx
  const out = {}
  for (const [k, v] of Object.entries(ctx)) {
    if (k === 'password' || k === 'token' || k === 'accessToken' || k === 'refreshToken' || k === 'secret') {
      out[k] = '***redacted***'
      continue
    }
    if (typeof v === 'string') {
      out[k] = sanitizeString(v)
    } else if (v && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date)) {
      out[k] = sanitizeCtx(v)
    } else {
      out[k] = v
    }
  }
  return out
}

// ─── Persistencia de errors a Firestore ──────────────────────────────────
// Solo en prod. Cola in-memory con flush diferido para no bloquear el render.
const _pending = []
let _flushTimer = null

function _scheduleFlush() {
  if (_flushTimer) return
  _flushTimer = setTimeout(_flush, 2000)
}

// ⚠️ 16-ago-2026 — POR QUÉ ESTE FLUSH NO PUEDE USAR `Promise.all` A SECAS.
// `addDoc` VALIDA EL DOCUMENTO DE FORMA SINCRÓNICA: si un campo top-level es
// `undefined`, tira ANTES de devolver la promesa (`parseSetData` corre antes
// del `executeWrite(...).then()`). O sea que el `.catch()` encadenado nunca se
// engancha: la excepción escapa del `.map`, `Promise.all` ni llega a llamarse,
// y como el lote ya fue `splice`-ado de la cola, **se pierde todo lo que venía
// después de la entrada mala**.
//
// Por eso cada write se envuelve en una promesa ANTES de invocarlo: así un
// throw sincrónico se convierte en rechazo y `allSettled` lo aísla. Mismo
// patrón que `permisosAuditTriggers.js` ("un doc que ya no existe no tumba el
// resto") y `AsignacionDetallePage.jsx`.
//
// Y no se reintenta: un documento que Firestore rechaza por estructura lo va a
// rechazar siempre — reintentarlo es un loop. Se descarta por entrada y se
// cuenta, que es lo contrario de lo que había: un `catch {}` mudo que perdía
// logs sin dejar rastro de por qué.
async function _flush() {
  _flushTimer = null
  if (_pending.length === 0) return
  const batch = _pending.splice(0, 20)  // máx 20 por flush
  const resultados = await Promise.allSettled(
    batch.map(entry => Promise.resolve().then(() => addDoc(collection(db, 'clientLogs'), entry))),
  )
  const fallidos = resultados.filter(r => r.status === 'rejected')
  if (fallidos.length > 0) {
    // Uno por flush con el conteo, no uno por entrada: si Firestore rechaza
    // sistemáticamente (un usuario sin sesión, que las rules rechazan), un
    // aviso por entrada convertiría el diagnóstico en el ruido.
    // No reentra: `_log` sólo escribe a consola, no persiste.
    _log('warn', 'logger', [
      `no se pudieron persistir ${fallidos.length}/${batch.length} logs`,
      fallidos[0].reason?.message || fallidos[0].reason,
    ])
  }
  if (_pending.length > 0) _scheduleFlush()
}

/**
 * Firestore rechaza cualquier campo con valor `undefined` —este proyecto no
 * habilita `ignoreUndefinedProperties`— y lo hace tirando SINCRÓNICO, así que
 * una sola clave así envenena el flush entero (ver `_flush`).
 *
 * El documento se arma con datos que vienen del caller, y `ctx` es opcional
 * por contrato: que falte es lo NORMAL, no un error. Sacar las claves sin
 * valor no es bloat defensivo — es la única forma de que el productor no pueda
 * emitir un documento inválido, que es donde vivía el bug.
 */
function _sinUndefined(obj) {
  const out = {}
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v
  }
  return out
}

function _persistError(scope, message, errObj, ctx) {
  if (!isProd) return  // dev: solo console
  try {
    const entry = {
      scope,
      level: 'error',
      message: sanitizeString(message || ''),
      errorName: errObj?.name || null,
      errorMessage: sanitizeString(errObj?.message || ''),
      errorStack: errObj?.stack ? sanitizeString(errObj.stack).slice(0, 4000) : null,
      ctx: sanitizeCtx(ctx),
      url: urlSegura(window.location?.href),
      userAgent: window.navigator?.userAgent || null,
      uid: auth.currentUser?.uid || null,
      email: auth.currentUser?.email || null,
      timestamp: serverTimestamp(),
      // V3.6.4 — TTL 30 días. Errors viejos no aportan; storage hygiene.
      expiresAt: Timestamp.fromMillis(Date.now() + 30 * 86400 * 1000),
    }
    _pending.push(_sinUndefined(entry))
    if (_pending.length >= 20) _flush()
    else _scheduleFlush()
  } catch {
    // graceful
  }
}

// ─── API pública ──────────────────────────────────────────────────────────

function _log(level, scope, args) {
  const prefix = scope ? `[${scope}]` : ''
  const fn = (
    level === 'debug' ? console.debug :
    level === 'info' ? console.info :
    level === 'warn' ? console.warn :
    console.error
  )
  if (prefix) fn(prefix, ...args)
  else fn(...args)
}

export function createLogger(scope) {
  return {
    debug(...args) {
      if (!debugEnabled()) return
      _log('debug', scope, args)
    },
    info(...args) {
      if (!isDev) return  // info silent en prod
      _log('info', scope, args)
    },
    warn(...args) {
      _log('warn', scope, args)
    },
    error(message, errObj, ctx) {
      const args = errObj ? [message, errObj, ctx].filter(x => x !== undefined) : [message]
      _log('error', scope, args)
      _persistError(scope, message, errObj, ctx)
    },
  }
}

// Logger default (sin scope) para callsites sueltos.
export const log = createLogger(null)

// Helper para diagnóstico manual en dev tools:
// window.__cowork_logger.flush()
if (typeof window !== 'undefined' && isDev) {
  window.__cowork_logger = {
    flush: _flush,
    pending: () => _pending.slice(),
    sanitizeCtx,
  }
}
